import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import {
  listenToUserCommercePartners,
  setCurrentPartner,
} from "../../../redux/commercePartnersSlice";
import { logout } from "../../../redux/authSlice";
import appLogo from "../../../assets/app/logo.png";

// ✅ Fresh sub-pages
import StoreFresh from "./FreshStore";
import FreshCustomers from "./FreshCustomers";
import FreshFinance from "./FreshFinance";
import FreshAnalytics from "./FreshAnalytics";
import type { Vertical } from "./FreshStore";


import {
  Apple, ShoppingCart, Users, TrendingUp, Tag, DollarSign, BarChart2,
  Search, ChevronDown, ChevronLeft, ChevronRight, Settings, LogOut, Plus, X,
  LayoutDashboard, Pencil, MapPin, Bike, Sprout,
} from "lucide-react";
import FreshHostOrders from "./FreshHostOrders";

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — same palette as StoreCentral
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg:        "#FBF6EE",
  panelBg:   "#FFFFFF",
  panel2:    "#FFF9EF",
  dark:      "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",
  red:       "#C8202E",
  redDark:   "#9E1521",
  redSoft:   "#FBE7E9",
  gold:      "#F2A93E",
  goldDark:  "#D4900F",
  goldSoft:  "#FCEFD4",
  green:     "#3F8A4C",
  greenSoft: "#E7F3E8",
  border:    "#EEDFC4",
  divider:   "#F3E6CE",
};

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

const SIDEBAR_W = 224;
const SIDEBAR_W_COLLAPSED = 72;

/* ═══════════════════════════════════════════════════════════
   CSS-in-JS — `fc-` prefix so it can't collide with StoreCentral's
   `sc-` rules if both ever mount in the same tree.
═══════════════════════════════════════════════════════════ */
const FC_CSS = `
  .fc-root{font-family:'Montserrat','Cormorant Garamond',sans-serif;background:${HEART.bg};}
  .fc-root ::-webkit-scrollbar{width:8px;height:8px;}
  .fc-root ::-webkit-scrollbar-thumb{background:${HEART.border};border-radius:8px;}
  .fc-root button{font-family:inherit;}

  @keyframes fcRing{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes fcPop{from{opacity:0;transform:translateY(8px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}
  @keyframes fcWiggle{0%,100%{transform:rotate(-4deg);}50%{transform:rotate(4deg);}}
  @keyframes fcBob{0%,100%{transform:translateY(0);}50%{transform:translateY(-4px);}}

  .fc-navitem{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:11px 12px;border-radius:14px;color:${HEART.dark};font-weight:700;
    font-size:14px;text-align:left;transition:background .15s,transform .1s,box-shadow .15s;
  }
  .fc-navitem:hover{background:${HEART.goldSoft};}
  .fc-navitem.active{
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
    color:#fff;
    box-shadow:0 6px 16px rgba(200,32,46,.35);
  }
  .fc-navitem:active{transform:scale(0.98);}
  .fc-navitem.collapsed{justify-content:center;padding:11px;}

  .fc-navicon{
    width:32px;height:32px;border-radius:11px;background:${HEART.goldSoft};display:flex;
    align-items:center;justify-content:center;flex-shrink:0;color:${HEART.redDark};transition:background .15s,color .15s;
  }
  .fc-navitem.active .fc-navicon{background:rgba(255,255,255,.25);color:#fff;}

  .fc-logout{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:11px 12px;border-radius:14px;color:${HEART.red};font-weight:700;
    font-size:13.5px;text-align:left;transition:background .15s;
  }
  .fc-logout:hover{background:${HEART.redSoft};}
  .fc-logout.collapsed{justify-content:center;padding:11px;}

  .fc-settings{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:11px 12px;border-radius:14px;color:${HEART.textMuted};font-weight:700;
    font-size:13.5px;text-align:left;transition:background .15s;
  }
  .fc-settings:hover{background:${HEART.goldSoft};color:${HEART.dark};}
  .fc-settings.collapsed{justify-content:center;padding:11px;}

  .fc-collapse-toggle{
    display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:10px;
    background:${HEART.goldSoft};border:none;color:${HEART.redDark};cursor:pointer;flex-shrink:0;
    transition:background .15s,color .15s,transform .1s;
  }
  .fc-collapse-toggle:hover{background:${HEART.gold};color:#fff;}
  .fc-collapse-toggle:active{transform:scale(0.92);}

  .fc-panelcard{animation:fcPop .25s ease both;}

  .fc-ringOuter{position:relative;display:inline-flex;border-radius:14px;flex-shrink:0;}
  .fc-ringBorder{
    position:absolute;inset:-2px;border-radius:16px;
    background:linear-gradient(90deg, ${HEART.dark}, ${HEART.gold}, ${HEART.green}, ${HEART.gold}, ${HEART.dark});
    background-size:300% 100%;animation:fcRing 3s linear infinite;z-index:0;
  }
  .fc-ringInner{position:relative;z-index:1;background:#fff;border-radius:14px;padding:3px;display:inline-flex;}
  .fc-logoimg{width:30px;height:30px;border-radius:10px;object-fit:cover;display:block;}
  .fc-wiggle{animation:fcWiggle 2.6s ease-in-out infinite;transform-origin:center;display:inline-flex;}
  .fc-bob{animation:fcBob 1.6s ease-in-out infinite;}

  .fc-accountbtn{
    display:flex;align-items:center;gap:8px;border-radius:999px;border:1px solid ${HEART.border};
    background:#fff;padding:5px 14px 5px 5px;cursor:pointer;box-shadow:0 3px 10px rgba(27,14,6,.10);
    transition:box-shadow .15s,transform .1s;
  }
  .fc-accountbtn:hover{box-shadow:0 5px 14px rgba(27,14,6,.16);}
  .fc-accountbtn:active{transform:translateY(1px);}

  .fc-createrow{
    display:flex;align-items:center;gap:10px;width:100%;background:transparent;border:none;
    border-radius:14px;padding:9px 12px;cursor:pointer;text-align:left;transition:background .15s;
  }
  .fc-createrow:hover{background:${HEART.goldSoft};}

  /* ── shop-type badge: Bakery / Butcher shop / Fresh market… ── */
  .fc-scopebadge{
    display:inline-flex;align-items:center;gap:5px;font-size:10.5px;font-weight:800;
    color:${HEART.green};background:${HEART.greenSoft};border:1px solid ${HEART.border};
    border-radius:999px;padding:3px 10px;white-space:nowrap;letter-spacing:.02em;
  }

  /* ── search ── */
  .fc-searchwrap{position:relative;}
  .fc-searchclear{
    display:flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:999px;
    background:${HEART.border};border:none;color:${HEART.textMuted};cursor:pointer;flex-shrink:0;
  }
  .fc-searchresults{
    position:absolute;top:calc(100% + 8px);left:0;right:0;background:#fff;border:1px solid ${HEART.border};
    border-radius:14px;box-shadow:0 14px 40px rgba(27,14,6,.16);z-index:60;overflow:hidden;padding:6px;
  }
  .fc-searchresultrow{
    display:flex;align-items:center;gap:10px;width:100%;background:transparent;border:none;
    border-radius:10px;padding:9px 10px;cursor:pointer;text-align:left;transition:background .12s;
    font-size:13px;font-weight:700;color:${HEART.dark};
  }
  .fc-searchresultrow:hover{background:${HEART.goldSoft};}
  .fc-searchempty{padding:12px 10px;font-size:12px;color:${HEART.textFaint};font-weight:600;}

  /* mobile bottom tab bar — first 5 fill the view, the rest scroll */
  .fc-tabbar{
    position:fixed;left:0;right:0;bottom:0;display:flex;overflow-x:auto;
    background:${HEART.panelBg};border-top:1px solid ${HEART.border};z-index:50;
    padding-bottom:env(safe-area-inset-bottom);box-shadow:0 -4px 16px rgba(27,14,6,0.08);
    scrollbar-width:none;
  }
  .fc-tabbar::-webkit-scrollbar{display:none;}
  .fc-tab{
    flex:0 0 20%;min-width:68px;display:flex;flex-direction:column;align-items:center;justify-content:center;
    gap:3px;padding:9px 4px 10px;background:transparent;border:none;cursor:pointer;color:${HEART.textFaint};
    transition:color .15s;
  }
  .fc-tab.active{color:${HEART.red};}
  .fc-tab span{font-size:9px;font-weight:800;white-space:nowrap;}
  .fc-tabicon{
    width:30px;height:30px;border-radius:11px;display:flex;align-items:center;justify-content:center;
    background:transparent;transition:background .15s,color .15s;
  }
  .fc-tab.active .fc-tabicon{background:${HEART.redSoft};}

  .fc-desktop-only{display:none}
  .fc-mobile-only{display:block}
  @media(min-width:900px){
    .fc-desktop-only{display:flex !important}
    .fc-mobile-only{display:none !important}
    .fc-tabbar{display:none !important}
  }
`;

/* ═══════════════════════════════════════════════════════════
   TYPES + NAV
   "items" replaces StoreCentral's "products" — a bakery stocks items,
   not products — and Delivery zones is promoted to a real tab because
   perishables live or die on the delivery radius.
═══════════════════════════════════════════════════════════ */
type FreshTab =
  | "items" | "orders" | "customers" | "delivery"
  | "discounts" | "growth" | "finance" | "analytics";

interface NavItem {
  id: FreshTab;
  label: string;
  icon: React.ReactNode;
  keywords: string[];
}

const NAV_ITEMS: NavItem[] = [
  { id: "items",     label: "Items",     icon: <Apple size={16} />,        keywords: ["items", "products", "stock", "shelf", "inventory", "catalog", "produce"] },
  { id: "orders",    label: "Orders",    icon: <ShoppingCart size={16} />, keywords: ["orders", "sales", "baskets", "checkout", "pickup"] },
  { id: "customers", label: "Customers", icon: <Users size={16} />,        keywords: ["customers", "clients", "buyers", "shoppers"] },
  { id: "delivery",  label: "Delivery",  icon: <Bike size={16} />,         keywords: ["delivery", "zones", "radius", "dispatch", "courier", "shipping"] },
  { id: "discounts", label: "Discounts", icon: <Tag size={16} />,          keywords: ["discounts", "coupons", "deals", "markdown", "clearance", "promo code"] },
  { id: "growth",    label: "Growth",    icon: <TrendingUp size={16} />,   keywords: ["growth", "marketing", "promo", "campaigns"] },
  { id: "finance",   label: "Finance",   icon: <DollarSign size={16} />,   keywords: ["finance", "payouts", "revenue", "earnings", "billing"] },
  { id: "analytics", label: "Analytics", icon: <BarChart2 size={16} />,    keywords: ["analytics", "stats", "reports", "insights", "waste", "charts"] },
];

const TAB_IDS = NAV_ITEMS.map((n) => n.id);
const isFreshTab = (v?: string): v is FreshTab => !!v && (TAB_IDS as string[]).includes(v);

/* Shop-type label per PartnerCategories subcategoryId. Mirrors the
   FRESH_SCOPE table inside StoreFresh — kept as a plain lookup here so
   the shell has no reason to import the editor's internals. */
const SHOP_LABEL: Record<number, string> = {
  101: "Grocery store",
  102: "Fresh market",
  103: "Bakery",
  104: "Butcher shop",
  105: "Seafood market",
  106: "Beverage shop",
  107: "Organic store",
  108: "Fast food",
};

/** Every subcategory this dashboard is the correct home for. */
const FRESH_IDS = Object.keys(SHOP_LABEL).map(Number);

/** PartnerCategories subcategoryId → which FreshStore vertical to render. */
const SHOP_VERTICAL: Record<number, Vertical> = {
  101: "grocery",  // Grocery Stores
  102: "grocery",  // Fresh Markets
  103: "grocery",  // Bakeries
  104: "grocery",  // Butcher Shops
  105: "grocery",  // Seafood Markets
  106: "grocery",  // Beverage Shops
  107: "grocery",  // Organic Stores
  108: "fastfood", // Fast Foods
};

/** Vertical → the real CategoriesCollection branch id under Food ("08"). */
const VERTICAL_BRANCH_ID: Record<Vertical, string> = {
  fastfood: "0801",
  grocery:  "0802",
  farm:     "0803",
};


/* helper — 2-letter initials fallback for shops without a logo */
const storeInitials = (name?: string) =>
  (name || "My Shop")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

/* ═══════════════════════════════════════════════════════════
   PER-CATEGORY DASHBOARD ROUTING — identical table to StoreCentral,
   so switching brands from either shell lands in the same place.
═══════════════════════════════════════════════════════════ */
const getDashboardRoute = (
  subcategoryId: number | string | undefined | null,
  brandId: string,
  fallbackTab: string = "products"
): string => {
  switch (Number(subcategoryId)) {
    // ── Shopping (1) — food & fresh all share FoodDashboard ──
    case 101: case 102: case 103: case 104:
    case 105: case 106: case 107: case 108:
      return `/freshstore/${brandId}/items`;

    // ── Booking (2) ──
    case 201: return `/DriverDashboard/${brandId}`;      // Ride
    case 203: return `/shortdashboard/${brandId}`;       // Vacation Rentals
    case 204: return `/cleaningdashboard/${brandId}`;    // Cleanings
    case 205: return `/vacantdashboard/${brandId}`;      // Vacant Rentals
    case 206: return `/hoteldashboard/${brandId}`;       // Hotels
    case 207: return `/RestaurantDashboard/${brandId}`;  // Restaurants

    // ── Ticket (202) — each sub-type has its own dashboard ──
    case 202:   return `/TicketDashboard/${brandId}`;        // legacy / unspecified
    case 20201: return `/TrainDashboard/${brandId}`;         // Train
    case 20202: return `/BusDashboard/${brandId}`;           // Bus & Coach
    case 20203: return `/FlightDashboard/${brandId}`;        // Air
    case 20204: return `/FerryDashboard/${brandId}`;         // Ferry & Boat
    case 20205: return `/CinemaDashboard/${brandId}`;        // Cinema
    case 20206: return `/ThemeParkDashboard/${brandId}`;     // Theme Park
    case 20207: return `/ConcertDashboard/${brandId}`;       // Concerts
    case 20208: return `/SportsEventDashboard/${brandId}`;   // Sports Events
    case 20209: return `/AttractionDashboard/${brandId}`;    // Attractions

    // ── Classifieds (3) ──
    case 301: return `/propertydashboard/${brandId}`;    // Properties
    case 302: return `/automotivedashboard/${brandId}`;  // Automotive

    // ── Everything else lives inside StoreCentral ──
    default: return `/store/${brandId}/${fallbackTab}`;
  }
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   Route to register:  /freshstore/:brandId/:tab?/:listingId?
═══════════════════════════════════════════════════════════ */
const FreshStoreCentral: React.FC = () => {
  const { brandId, tab, listingId } = useParams<{ brandId: string; tab?: string; listingId?: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const currentUser = useSelector((state: RootState) => state.auth.user);
  const authLoaded = useSelector((state: RootState) => (state.auth as any).authLoaded ?? true);
  const { currentPartner, loading } = useSelector(
    (state: RootState) => state.commercePartners
  );
  /* owner-scoped only — `partners` is the platform-wide browse list */
  const partners = useSelector(
    (state: RootState) => (state.commercePartners as any).myPartners ?? []
  );
  const myPartnersLoaded = useSelector(
    (state: RootState) => (state.commercePartners as any).myPartnersLoaded ?? false
  );

  /* is this brand actually mine? */
  const ownsBrand = !brandId || partners.some((p: any) => p.brandId === brandId);

  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const activeTab: FreshTab = isFreshTab(tab) ? tab : "items";
  const subcategoryId = (currentPartner as any)?.subcategoryId;
  const shopLabel = SHOP_LABEL[Number(subcategoryId)] ?? "Fresh & food";

  /* signed out → nothing here is viewable */
  useEffect(() => {
    if (authLoaded && !currentUser?.userId) {
      navigate("/AuthSwitch", { replace: true });
    }
  }, [authLoaded, currentUser?.userId, navigate]);

  useEffect(() => {
    if (!currentUser?.userId) return;
    const unsub = dispatch(listenToUserCommercePartners(currentUser.userId) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [currentUser?.userId, dispatch]);

  useEffect(() => {
    if (!brandId || !myPartnersLoaded) return;
    const mine = partners.find((p: any) => p.brandId === brandId);
    dispatch(setCurrentPartner(mine ?? null));
  }, [brandId, partners, myPartnersLoaded, dispatch]);

  // Landing on /FoodDashboard with no brandId: pick the user's first
  // *fresh* brand rather than whatever happens to be first overall.
  useEffect(() => {
    if (brandId || !myPartnersLoaded || partners.length === 0) return;
    const fresh = partners.find((p: any) => FRESH_IDS.includes(Number(p.subcategoryId)));
    const target = fresh ?? partners[0];
    navigate(getDashboardRoute((target as any).subcategoryId, target.brandId, "items"), { replace: true });
  }, [brandId, partners, myPartnersLoaded, navigate]);

  // Wrong shell for this brand — a hotel or a ride shouldn't render a
  // grocery editor just because someone typed the URL.
  useEffect(() => {
    if (!currentPartner || currentPartner.brandId !== brandId) return;
    if (FRESH_IDS.includes(Number(subcategoryId))) return;
    navigate(getDashboardRoute(subcategoryId, brandId!, "products"), { replace: true });
  }, [currentPartner, subcategoryId, brandId, navigate]);

  // A bare /freshstore/:brandId gets pushed onto the Items tab so the
  // URL always names the section you're looking at.
  useEffect(() => {
    if (brandId && !tab) navigate(`/freshstore/${brandId}/items`, { replace: true });
  }, [brandId, tab, navigate]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowAccountMenu(false);
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowSearchResults(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // ⌘K / Ctrl+K focuses search from anywhere on the page
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        setShowSearchResults(true);
      }
      if (e.key === "Escape") {
        setShowSearchResults(false);
        searchInputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleTabChange = (tabId: FreshTab) => navigate(`/freshstore/${brandId}/${tabId}`);

  // Takes the whole partner — destination depends on its subcategoryId,
  // so switching to a hotel brand leaves this shell entirely.
  const handleStoreSwitch = (partner: any) => {
    setShowAccountMenu(false);
    navigate(getDashboardRoute(partner.subcategoryId, partner.brandId, "items"));
  };

  const handleCreateStore = () => {
    setShowAccountMenu(false);
    navigate(`/onboarding/${currentUser?.userId}`);
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate("/login");
  };

  const searchResults = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return NAV_ITEMS.filter(
      (item) => item.label.toLowerCase().includes(q) || item.keywords.some((k) => k.includes(q))
    );
  })();

  const goToSearchResult = (item: NavItem) => {
    handleTabChange(item.id);
    setSearchQuery("");
    setShowSearchResults(false);
    searchInputRef.current?.blur();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchResults.length > 0) goToSearchResult(searchResults[0]);
  };

  const renderComponent = () => {
  const activeVertical: Vertical = SHOP_VERTICAL[Number(subcategoryId)] ?? "fastfood";
  const scopeId = VERTICAL_BRANCH_ID[activeVertical];

  switch (activeTab) {
    case "items":
      return (
        <StoreFresh
          brandId={brandId!}
          listingId={listingId}
          vertical={activeVertical}
          subcategoryId={scopeId}
          basePath={`/freshstore/${brandId}/items`}
        />
      );

    case "customers":
      return <FreshCustomers brandId={brandId!} />;
    case "finance":
      return <FreshFinance brandId={brandId!} />;
    case "analytics":
      return <FreshAnalytics brandId={brandId!} />;
    case "orders":    return <FreshHostOrders brandId={brandId!} />;

    default:
      return (
        <StoreFresh
          brandId={brandId!}
          vertical={activeVertical}
          subcategoryId={scopeId}
          basePath={`/freshstore/${brandId}/items`}
        />
      );
  }
};

  /* shop logo tile — account button + every row in the switcher */
  const StoreTile: React.FC<{ partner: any; size?: number }> = ({ partner, size = 30 }) => {
    const logo = partner?.image || partner?.brandLogo || partner?.logoUrl || partner?.logo;
    return logo ? (
      <img
        src={logo}
        alt={partner?.name || "Shop"}
        onError={(e) => {
          const el = e.currentTarget as HTMLImageElement;
          el.onerror = null;
          el.style.display = "none";
          const fallback = el.nextElementSibling as HTMLElement | null;
          if (fallback) fallback.style.display = "flex";
        }}
        style={{ width: size, height: size, borderRadius: size * 0.32, objectFit: "cover", flexShrink: 0, border: `1px solid ${HEART.border}` }}
      />
    ) : (
      <span
        style={{
          width: size, height: size, borderRadius: size * 0.32, display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${HEART.gold}, ${HEART.goldDark})`, color: "#fff",
          fontWeight: 800, fontSize: size * 0.4, flexShrink: 0,
        }}
      >
        {storeInitials(partner?.name)}
      </span>
    );
  };

  /* ── waiting on auth or the ownership list ── */
  if (!authLoaded || (currentUser?.userId && !myPartnersLoaded)) {
    return (
      <div className="fc-root" style={S.loaderPage}>
        <style>{FONT_CSS + FC_CSS}</style>
        <div style={{ textAlign: "center" }}>
          <div className="fc-bob" style={S.loaderBubble}><Sprout size={22} color="#fff" /></div>
          <p style={{ color: HEART.textMuted, fontWeight: 700, marginTop: 14 }}>Checking your access…</p>
        </div>
      </div>
    );
  }

  /* ── signed in, but this shop isn't theirs ── */
  if (brandId && !ownsBrand) {
    return (
      <div className="fc-root" style={S.loaderPage}>
        <style>{FONT_CSS + FC_CSS}</style>
        <div className="fc-panelcard" style={S.emptyCard}>
          <p style={{ fontSize: 20, fontWeight: 700, color: HEART.dark, marginBottom: 8, fontFamily: "'Cormorant Garamond', serif" }}>
            This shop isn't yours
          </p>
          <p style={{ fontSize: 13, color: HEART.textMuted, marginBottom: 20, maxWidth: 300 }}>
            Fresh Studio only opens shops on your own account. Check you're signed in with the right one.
          </p>
          <button
            style={S.primaryBtn}
            onClick={() => navigate(partners.length ? "/freshstore" : `/onboarding/${currentUser?.userId}`)}
          >
            {partners.length ? "Go to my shops" : "Create a shop"}
          </button>
        </div>
      </div>
    );
  }

  /* ── loading / empty states ── */
  if (loading && !currentPartner) {
    return (
      <div className="fc-root" style={S.loaderPage}>
        <style>{FONT_CSS + FC_CSS}</style>
        <div style={{ textAlign: "center" }}>
          <div className="fc-bob" style={S.loaderBubble}>
            <Sprout size={22} color="#fff" />
          </div>
          <p style={{ color: HEART.textMuted, fontWeight: 700, marginTop: 14 }}>Opening your shop…</p>
        </div>
      </div>
    );
  }

  if (!currentPartner) {
    return (
      <div className="fc-root" style={S.loaderPage}>
        <style>{FONT_CSS + FC_CSS}</style>
        <div className="fc-panelcard" style={S.emptyCard}>
          <p style={{ fontSize: 20, fontWeight: 700, color: HEART.dark, marginBottom: 8, fontFamily: "'Cormorant Garamond', serif" }}>
            No shop found yet 🌱
          </p>
          <p style={{ fontSize: 13, color: HEART.textMuted, marginBottom: 18 }}>
            Register a brand to start stocking shelves.
          </p>
          <button style={S.primaryBtn} onClick={() => navigate(`/onboarding/${currentUser?.userId}`)}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  /* shop switcher card — 3D style matching CommerceOnboarding's PartnerCard */
  const StoreSwitchCard: React.FC<{ partner: any; isCurrent: boolean }> = ({ partner, isCurrent }) => {
    const type = SHOP_LABEL[Number(partner.subcategoryId)];
    return (
      <div
        style={{
          display: "flex", alignItems: "center", gap: 12,
          background: "#fff", borderRadius: 16, border: `1.5px solid ${HEART.border}`,
          boxShadow: "0 4px 0 #E7D9B8, 0 8px 20px rgba(27,14,6,.08)",
          padding: "12px 14px", marginBottom: 10,
        }}
      >
        <div style={{ width: 3, height: 46, background: isCurrent ? HEART.red : HEART.gold, flexShrink: 0, borderRadius: 2 }} />
        <StoreTile partner={partner} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: HEART.dark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {partner.name || "Shop"}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
            {type && <span className="fc-scopebadge">{type}</span>}
            {partner.address?.city && (
              <span style={{ fontSize: 10, color: HEART.textMuted, display: "flex", alignItems: "center", gap: 3 }}>
                <MapPin size={9} /> {partner.address.city}
              </span>
            )}
            {isCurrent && (
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", color: "#fff", background: HEART.red, padding: "2px 8px", borderRadius: 999 }}>
                CURRENT
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
          <button
            style={{ background: `linear-gradient(135deg, ${HEART.gold}, ${HEART.goldDark})`, border: "none", color: "#fff", padding: "7px 12px", cursor: "pointer", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 5, borderRadius: 8, whiteSpace: "nowrap" }}
            onClick={() => handleStoreSwitch(partner)}
          >
            <LayoutDashboard size={11} /> DASHBOARD
          </button>
          <button
            style={{ background: "#fff", border: `1.5px solid ${HEART.border}`, color: HEART.dark, padding: "7px 12px", cursor: "pointer", fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 5, borderRadius: 8, whiteSpace: "nowrap" }}
            onClick={() => { setShowAccountMenu(false); navigate(`/onboarding/${partner.brandId}`); }}
          >
            <Pencil size={11} /> EDIT
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fc-root" style={S.page}>
      <style>{FONT_CSS + FC_CSS}</style>

      {/* ═══ TOP BAR ═══ */}
      <div style={S.topbar}>
        <div style={S.brandRow}>
          <span className="fc-ringOuter">
            <span className="fc-ringBorder" />
            <span className="fc-ringInner">
              <span className="fc-wiggle">
                <img
                  src={appLogo}
                  alt="Heartlax"
                  className="fc-logoimg"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                />
              </span>
            </span>
          </span>
          <div className="fc-desktop-only" style={{ flexDirection: "column" }}>
            <div style={S.brandName}>Heartlax</div>
            <div style={S.brandSub}>Fresh Studio</div>
          </div>
        </div>

        {/* live search — desktop only */}
        <div className="fc-desktop-only fc-searchwrap" style={{ flex: 1, maxWidth: 340 }} ref={searchRef}>
          <div style={S.searchBar}>
            <Search size={14} color={HEART.textFaint} />
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setShowSearchResults(true); }}
              onFocus={() => setShowSearchResults(true)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search"
              style={S.searchInput}
            />
            {searchQuery ? (
              <button className="fc-searchclear" onClick={() => { setSearchQuery(""); searchInputRef.current?.focus(); }} aria-label="Clear search">
                <X size={11} />
              </button>
            ) : (
              <kbd style={S.kbd}>⌘K</kbd>
            )}
          </div>

          {showSearchResults && searchQuery && (
            <div className="fc-panelcard fc-searchresults">
              {searchResults.length > 0 ? (
                searchResults.map((item) => (
                  <button key={item.id} className="fc-searchresultrow" onClick={() => goToSearchResult(item)}>
                    <span style={{ ...S.navIconSmall, background: HEART.goldSoft, color: HEART.redDark }}>{item.icon}</span>
                    {item.label}
                  </button>
                ))
              ) : (
                <div className="fc-searchempty">No matches for "{searchQuery}"</div>
              )}
            </div>
          )}
        </div>

        {/* account pill + dropdown */}
        <div style={{ position: "relative" }} ref={menuRef}>
          <button className="fc-accountbtn" onClick={() => setShowAccountMenu((v) => !v)}>
            <StoreTile partner={currentPartner} />
            <span className="fc-desktop-only" style={{ flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: HEART.dark, lineHeight: 1.1 }}>
                {currentPartner.name || "My Shop"}
              </span>
              <span style={{ fontSize: 10, fontWeight: 700, color: HEART.textFaint }}>{shopLabel}</span>
            </span>
            <ChevronDown
              size={12}
              color={HEART.textMuted}
              style={{ transform: showAccountMenu ? "rotate(180deg)" : "none", transition: "transform .15s" }}
            />
          </button>

          {showAccountMenu && (
            <div className="fc-panelcard" style={S.accountDropdown}>
              <div style={S.accountDropdownHeader}>
                <StoreTile partner={currentPartner} size={38} />
                <div style={{ minWidth: 0 }}>
                  <p style={S.truncBold}>{currentPartner.name || "My Shop"}</p>
                  <p style={S.truncMuted}>{currentPartner.brandId}</p>
                </div>
              </div>

              <button className="fc-createrow" onClick={handleCreateStore}>
                <span style={{ ...S.navIconSmall, background: HEART.goldSoft, color: HEART.redDark }}>
                  <Plus size={12} />
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: HEART.dark }}>Create store</span>
              </button>

              {partners.length > 0 && (
                <div style={{ maxHeight: 360, overflowY: "auto", borderTop: `1px solid ${HEART.divider}`, marginTop: 6, paddingTop: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".12em", color: HEART.textMuted, padding: "0 4px 8px" }}>
                    MY BRANDS
                  </div>
                  {partners.map((partner: any) => (
                    <StoreSwitchCard key={partner.brandId} partner={partner} isCurrent={partner.brandId === brandId} />
                  ))}
                </div>
              )}

              <div style={{ borderTop: `1px solid ${HEART.divider}`, marginTop: 4, paddingTop: 4 }}>
                <div style={{ padding: "8px 12px" }}>
                  <p style={S.truncBold}>{currentUser?.firstName} {currentUser?.lastName}</p>
                  <p style={S.truncMuted}>{currentUser?.email}</p>
                </div>
                <button className="fc-logout" onClick={handleLogout}>
                  <LogOut size={15} /> Log out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={S.body}>
        {/* ═══ DESKTOP SIDEBAR ═══ */}
        <aside className="fc-desktop-only" style={{ ...S.sidebar, width: sidebarOpen ? SIDEBAR_W : SIDEBAR_W_COLLAPSED }}>
          <div style={S.sidebarToggleRow}>
            {sidebarOpen && <span style={S.sidebarToggleLabel}>Menu</span>}
            <button
              className="fc-collapse-toggle"
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            >
              {sidebarOpen ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
            </button>
          </div>

          {sidebarOpen && (
            <div style={{ padding: "0 2px 4px" }}>
              <span className="fc-scopebadge"><Sprout size={11} /> {shopLabel}</span>
            </div>
          )}

          <nav style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3, overflowY: "auto" }}>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                className={`fc-navitem${activeTab === item.id ? " active" : ""}${!sidebarOpen ? " collapsed" : ""}`}
                onClick={() => handleTabChange(item.id)}
                title={item.label}
              >
                <span className="fc-navicon">{item.icon}</span>
                {sidebarOpen && item.label}
              </button>
            ))}
          </nav>

          <div style={{ borderTop: `1px solid ${HEART.divider}`, paddingTop: 8, display: "flex", flexDirection: "column", gap: 2 }}>
            <button className={`fc-settings${!sidebarOpen ? " collapsed" : ""}`} title="Settings">
              <Settings size={16} /> {sidebarOpen && "Settings"}
            </button>
            <button className={`fc-logout${!sidebarOpen ? " collapsed" : ""}`} onClick={handleLogout} title="Log out">
              <LogOut size={16} /> {sidebarOpen && "Log out"}
            </button>
          </div>
        </aside>

        {/* ═══ CONTENT ═══ */}
        <main className="fc-panelcard" style={S.content}>
          {renderComponent()}
        </main>
      </div>

      {/* ═══ MOBILE BOTTOM TAB BAR ═══ */}
      <div className="fc-tabbar">
        {NAV_ITEMS.map((item) => (
          <button
            key={item.id}
            className={`fc-tab${activeTab === item.id ? " active" : ""}`}
            onClick={() => handleTabChange(item.id)}
          >
            <span className="fc-tabicon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default FreshStoreCentral;

/* ═══════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════ */
const S: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: HEART.bg, color: HEART.dark },
  loaderPage: { minHeight: "100vh", background: HEART.bg, display: "flex", alignItems: "center", justifyContent: "center" },
  loaderBubble: {
    width: 56, height: 56, borderRadius: "50%", margin: "0 auto",
    background: `linear-gradient(135deg, ${HEART.green}, #2F6B3A)`,
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 8px 20px rgba(63,138,76,.35)",
  },
  emptyCard: {
    background: "#fff", border: `1px solid ${HEART.border}`, borderRadius: 20,
    padding: "36px 40px", textAlign: "center", boxShadow: "0 10px 30px rgba(27,14,6,.08)",
  },
  primaryBtn: {
    background: `linear-gradient(135deg, ${HEART.red}, ${HEART.redDark})`, color: "#fff",
    border: "none", borderRadius: 999, padding: "12px 28px", fontWeight: 700, fontSize: 13,
    cursor: "pointer", letterSpacing: "0.04em", boxShadow: "0 8px 20px rgba(200,32,46,.3)",
  },

  topbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
    padding: "10px 20px", background: HEART.panelBg, borderBottom: `1px solid ${HEART.border}`,
    position: "sticky", top: 0, zIndex: 40,
  },
  brandRow: { display: "flex", alignItems: "center", gap: 10 },
  brandName: { fontSize: 15, fontWeight: 800, color: HEART.dark, lineHeight: 1.1 },
  brandSub: { fontSize: 11, color: HEART.textMuted, fontWeight: 600 },

  searchBar: {
    display: "flex", alignItems: "center", gap: 8, background: HEART.goldSoft, borderRadius: 999,
    padding: "8px 14px", border: `1px solid ${HEART.border}`,
  },
  searchInput: {
    flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
    fontSize: 13, color: HEART.dark, fontFamily: "inherit",
  },
  kbd: {
    background: "#fff", borderRadius: 6, padding: "2px 6px", fontSize: 10, fontWeight: 800,
    color: HEART.textFaint, border: `1px solid ${HEART.border}`, flexShrink: 0,
  },

  accountDropdown: {
    position: "absolute", right: 0, top: "calc(100% + 10px)", width: 400, maxWidth: "calc(100vw - 24px)", zIndex: 50,
    background: "#fff", borderRadius: 20, padding: 10, border: `1px solid ${HEART.border}`,
    boxShadow: "0 14px 40px rgba(27,14,6,.16)",
  },
  accountDropdownHeader: {
    display: "flex", alignItems: "center", gap: 10, background: HEART.goldSoft,
    borderRadius: 14, padding: "10px 12px", marginBottom: 4,
  },
  truncBold: { fontSize: 13, fontWeight: 700, color: HEART.dark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  truncMuted: { fontSize: 11, color: HEART.textFaint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  navIconSmall: { width: 24, height: 24, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },

  body: { display: "flex", alignItems: "flex-start" },
  sidebar: {
    flexShrink: 0, flexDirection: "column", padding: 14, gap: 12,
    borderRight: `1px solid ${HEART.border}`, minHeight: "calc(100vh - 61px)",
    position: "sticky", top: 61, background: HEART.bg, transition: "width .18s ease", overflow: "hidden",
  },
  sidebarToggleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px 4px" },
  sidebarToggleLabel: { fontSize: 11, fontWeight: 800, color: HEART.textMuted, textTransform: "uppercase", letterSpacing: ".06em" },

  content: { flex: 1, minWidth: 0, padding: "24px 20px 100px" },
};
